<script setup lang="ts">
import ReactionsList from '@/components/reactions/ReactionsList.vue'
import { app } from '@/composition/app'
import { ChannelState } from '@/composition/channels'
import { CommentState } from '@/composition/comments'
import { reportContent } from '@/composition/contentReport'
import { GroupState } from '@/composition/groups'
import { ReactionState } from '@/composition/reactions'
import { UserState } from '@/composition/users'
import { checkChannelEditAccess } from '@/helper/channel.helper'
import {
  getShareableCommentLink,
  handleShareComment,
} from '@/helper/commentCard.helper'
import { DEFAULT_SAFE_AREA_INSET, Platform } from '@/helper/const'
import { getTextContent } from '@/helper/postCard.helper'
import { emitter } from '@/main'
import { IComments } from '@/models/Comments'
import { ContentType, IReaction, ReactionType } from '@/models/Reaction'
import { IUser } from '@/models/Users'
import { useAppStore } from '@/store/app'
import { getNumberAbbreviation } from '@/util/NumberUtils'
import { convertToRelativeTime } from '@/util/dateTimeConversion'
import { checkNativeKeyboardResize } from '@/util/helper'
import { ActionSheet, ActionSheetButtonStyle } from '@capacitor/action-sheet'
import { Capacitor, registerPlugin } from '@capacitor/core'
import { Haptics, ImpactStyle } from '@capacitor/haptics'
import { Share } from '@capacitor/share'
import { handleLinkClick } from '@ghl/clientportal-core'
import { isPwa } from '@ghl/clientportal-core/init'
import {
  AlertCircleIcon,
  DotsHorizontalIcon,
  Edit01Icon,
  FlipBackwardIcon,
  Share07Icon,
  Trash01Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UIDrawer,
  UIDrawerContent,
  UIDropdown,
  UIMenu,
  UIPopover,
  UITextMdMedium,
  UITextMdNormal,
  UITextSmMedium,
  UITextXsRegular,
  renderIcon,
} from '@gohighlevel/ghl-ui'
import debounce from 'lodash-es/debounce'
import {
  PropType,
  computed,
  defineAsyncComponent,
  h,
  nextTick,
  onBeforeUnmount,
  onMounted,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import { useCommunitiesToastNotification } from '../../helper/communitiesToastNotification.helper'
import AttachmentsPreview from '../common/AttachmentsPreview.vue'
import LikeButton from '../common/actionButtons/LikeButton.vue'
import PopoverAvatar from '../common/avatar/PopoverAvatar.vue'
import {
  addEventsListenerToMentions,
  openUserProfile,
  removeEventsListenersFromMentions,
} from '../helper'

const CommentEditor = defineAsyncComponent(() => import('./CommentEditor.vue'))

const CommentsAttachments = defineAsyncComponent(
  () => import('./CommentsAttachments.vue')
)

const ReactionsView = defineAsyncComponent(
  () => import('@/components/reactions/ReactionsView.vue')
)

const UIConfirmation = defineAsyncComponent(
  () => import('@/components/common/modal/UIConfirmation.vue')
)
const JoinGroupModal = defineAsyncComponent(
  () => import('@/components/common/modal/JoinGroupModal.vue')
)

const route = useRoute()
const router = useRouter()
const props = defineProps({
  comment: { type: [Object] as PropType<IComments>, required: true },
  mentionUserTooltip: {
    type: [Object] as PropType<HTMLElement | null>,
    required: true,
  },
  expandReplies: { type: Boolean, default: false },
  parentId: String,
  parentPostId: String,
  disableInteraction: Boolean,
  /** show comment action button like delete, edit, and like */
  isShowCommentAction: { type: Boolean, default: true },
  isTrimContent: { type: Boolean, default: false },
})
const appStore = useAppStore()
const { t } = useI18n()
const emit = defineEmits([
  'show:childCommentEditor',
  'delete-comment',
  'show:reply-comment-editor',
])
const showEditor = ref(false)
const showLikesModal = ref(false)
const commentContentRef = ref<HTMLElement | null>(null)
const replyToCommentAuthor = ref<IUser | undefined>(undefined)
const editComment = ref(false)
const commentCardRef = ref<HTMLElement | null>(null)
//Replies
const showReplies = ref(false)
const viewCommentText = ref(`View ${props.comment.repliesCount} Replies`)
const fetchingReplies = ref(false)
const loadingMoreReplies = ref(false)
const showJoinGroupModal = ref(false)
const showDeleteConfirmationModal = ref(false)
const showCommentPreview = ref(false)
const currentCommentIndex = ref(0)
const showCommentActionDrawer = ref(false)
const currentUser = ref<IUser | undefined>(undefined)
const commentEditorBottom = ref(0)
const notification = useCommunitiesToastNotification()
const showReportConfirmationModal = ref(false)
const isCommentHighlighted = ref(props.comment.isHighlighted)
const KeyboardEventsPlugin: any = registerPlugin('KeyboardEvents')

const hapticsImpactMedium = async () => {
  if (Capacitor.isNativePlatform()) {
    await Haptics.impact({ style: ImpactStyle.Medium })
  }
}

emitter.on('pwa:reply-comment-editor-is-closed', () => {
  replyToCommentAuthor.value = undefined
})

const CommentActionOption = computed(() => {
  //TODO: Structure and clean up this function
  if (
    props.comment.user?.contactId !== UserState.state.currentUser?.contactId &&
    !GroupState.state.isGroupAdmin
  ) {
    const menuList = [
      {
        label: isPwa() ? t('common.share') : t('common.copyLink'),
        key: 'copyComment',
        class: 'rounded-2xl',
        icon() {
          return h(renderIcon(Share07Icon), null, {})
        },
      },
    ]

    if (GroupState?.state?.role) {
      menuList.push({
        label: t('comments.reportComment'),
        key: 'reportComment',
        class: 'rounded-2xl',
        icon() {
          return h(renderIcon(AlertCircleIcon), null, {})
        },
      })
    }

    return menuList
  }

  const menuList = [
    {
      label: isPwa() ? t('common.share') : t('common.copyLink'),
      key: 'copyComment',
      class: 'rounded-2xl',
      icon() {
        return h(renderIcon(Share07Icon), null, {})
      },
    },
  ]
  if (
    !ChannelState.state.hasAnnouncementRestriction &&
    (checkChannelEditAccess() ||
      props.comment.user?.contactId === UserState.state.currentUser?.contactId)
  ) {
    menuList.push({
      label: t('comments.deleteComment'),
      key: 'deleteComment',
      class: 'rounded-2xl',
      icon() {
        return h(renderIcon(Trash01Icon), null, {})
      },
    })
  }
  if (
    !ChannelState.state.hasAnnouncementRestriction &&
    props.comment.user?.contactId === UserState.state.currentUser?.contactId
  ) {
    menuList.push({
      label: t('comments.editComment'),
      key: 'editComment',
      class: 'rounded-2xl',
      icon() {
        return h(renderIcon(Edit01Icon), null, {})
      },
    })
  }

  if (
    props.comment.user?.contactId !== UserState.state.currentUser?.contactId &&
    GroupState.isActiveMember()
  ) {
    menuList.push({
      label: t('comments.reportComment'),
      key: 'reportComment',
      class: 'rounded-2xl',
      icon() {
        return h(renderIcon(AlertCircleIcon), null, {})
      },
    })
  }

  return menuList
})

const actionSheetOptions = computed(() => {
  const options = CommentActionOption.value.map(item => {
    const option = { title: item.label }
    if (item.key === 'deleteComment') {
      option['style'] = ActionSheetButtonStyle.Destructive
    }
    return option
  })
  return options
})

const showActions = async () => {
  const result = await ActionSheet.showActions({
    options: [
      ...actionSheetOptions.value,
      {
        title: t('common.cancel'),
        style: ActionSheetButtonStyle.Cancel,
      },
    ],
  })
  const menuItem = CommentActionOption.value[result.index]
  if (menuItem) {
    handleCommentAction(menuItem.key)
  }
}

const CommentActionOptionsMobile = computed(() => {
  const menuList: any = []
  const currCommentActionOption = CommentActionOption
  currCommentActionOption.value.forEach((value, index) => {
    menuList.push(value)
    if (index < currCommentActionOption.value.length - 1)
      menuList.push({
        key: 'divider-1',
        type: 'divider',
        props: {},
      })
  })
  return menuList
})

onMounted(async () => {
  const hasNativeKeyboardResize = await checkNativeKeyboardResize(
    KeyboardEventsPlugin
  )

  if (Capacitor.getPlatform() === Platform.IOS && !hasNativeKeyboardResize) {
    KeyboardEventsPlugin.addListener('keyboardWillShow', info => {
      handleKeyboardWillShow(info.keyboardHeight)
    })

    KeyboardEventsPlugin.addListener('keyboardWillHide', () => {
      handleKeyboardWillHide()
    })
  }

  if ('virtualKeyboard' in navigator) {
    // @ts-ignore
    navigator.virtualKeyboard.overlaysContent = true
    // @ts-ignore
    navigator.virtualKeyboard.addEventListener(
      'geometrychange',
      handleGeometryChange
    )
  } else if (Capacitor.getPlatform() !== Platform.IOS) {
    // Handle layout changes in case virtualkeyboard API is not supported
    window.visualViewport?.addEventListener(
      'resize',
      handleVisualViewportResize
    )
  }

  if (props.expandReplies) {
    showReplies.value = true
    showCommentReplies()
  }
  addEventsToMentions()
  currentUser.value = await UserState.getUserByContactId(
    useAppStore().$state.userId
  )
  if (isCommentHighlighted.value) {
    setTimeout(() => {
      isCommentHighlighted.value = false
    }, 5000)
  }

  commentContentRef.value?.addEventListener('click', handleLinkClick)

  // Add event listener for closing edit mode when another comment is being edited
  emitter.on('close:comment-editors', () => {
    hideCommentEditor()
  })
})

onBeforeUnmount(() => {
  removeEventsFromMentions()
  window.visualViewport?.removeEventListener(
    'resize',
    handleVisualViewportResize
  )
  commentContentRef.value?.removeEventListener('click', handleLinkClick)

  // Remove the event listener when component is unmounted
  emitter.off('close:comment-editors')
})

// adding watcher to add events to mentions when every content is edited
watch(
  () => props.comment?.content,
  () => {
    setTimeout(() => {
      addEventsToMentions()
    }, 100)
  }
)

function handleGeometryChange(changeEvent) {
  const { height } = changeEvent.target.boundingRect
  commentEditorBottom.value = height
}

function handleVisualViewportResize() {
  if (
    window.visualViewport &&
    window.visualViewport?.height < window.innerHeight
  ) {
    // Keyboard likely is open.
    const keyboardHeight = window.innerHeight - window.visualViewport.height
    commentEditorBottom.value = keyboardHeight
    //document.body.style.position = 'fixed'
  } else if (window.visualViewport?.height == window.innerHeight) {
    // Keyboard likely is closed.
    commentEditorBottom.value = 0
    //document.body.style.position = ''
  }
}

function removeEventsFromMentions() {
  const mentions = commentContentRef.value?.querySelectorAll(
    'a[data-type="mention"]'
  )

  if (mentions) {
    removeEventsListenersFromMentions(mentions, props.mentionUserTooltip)
  }
}

function addEventsToMentions() {
  const mentions = commentContentRef.value?.querySelectorAll(
    'a[data-type="mention"]'
  )

  if (mentions) {
    // Add a mouse events listeners to the mentions
    addEventsListenerToMentions(mentions, props.mentionUserTooltip, router)
  }
}

const authorRole = computed(() => {
  return GroupState.state.groupAdmins.get(props.comment?.user?.contactId)?.role
})

const repliesNextPage = computed(() => {
  return CommentState.state.postCommentsNextPage.has(
    props.comment._id as string
  )
})
const replies = computed(() => {
  return CommentState.state.postComments.get(props.comment._id as string)
})

async function showCommentReplyEditor(replyToCommentAuthor1: any) {
  if (replyToCommentAuthor.value === replyToCommentAuthor1) {
    // If reply is to the same comment author, do nothing
    return
  }
  // close other comment editors when current device is mobile
  emitter.emit('pwa:reply-to-comment')
  if (app.state.isMobile) {
    emitter.emit('close:comment-editor')
  }
  // we are doing this to rerender the editor every time reply button is clicked
  showEditor.value = false
  editComment.value = false

  // we are string this value of replyToCommentAuthor and passing it in event to use it in comment editor
  // to always tag the member who's comment the user is trying to respond to
  replyToCommentAuthor.value = replyToCommentAuthor1
  // emit('show:reply-comment-editor')
  emitter.emit('pwa:hide-comment-editor')
  if (props.comment._id) {
    showCommentReplies()
    emit('show:childCommentEditor', replyToCommentAuthor1)
  }

  showEditor.value = appStore.isAuthenticated && GroupState.isActiveMember()
  if (!showEditor.value) {
    replyToCommentAuthor.value = undefined
    showJoinGroupModal.value = true
  }

  if (!app.state.isMobile) {
    nextTick(() => {
      // Code to execute after rendering
      const commentEditor = document.getElementById(
        (props.comment._id as string) + '_editor'
      )
      if (commentEditor) {
        commentEditor.style.paddingLeft = '48px'
        commentEditor.style.transform = 'scale(0.8)'
        commentEditor.style.transition = 'transform 0.3s, scroll-behavior 0.2s'

        setTimeout(() => {
          commentEditor.scrollIntoView({ behavior: 'smooth', block: 'center' })
          commentEditor.style.transform = 'scale(1)'
        }, 100)
      }
    })
  } else {
    // If it's Mobile and virtualKeyboard is not supported, counter scroll
    if (
      !('virtualKeyboard' in navigator) &&
      Capacitor.getPlatform() !== Platform.IOS
    )
      setTimeout(() => {
        window.scrollTo({
          top: 0,
          behavior: 'smooth',
          left: 0,
        })
        const commentEditor = document.getElementById(
          (props.comment._id as string) + '_editor'
        )
        if (commentEditor && window.visualViewport) {
          commentEditor.style.bottom = `${
            window.innerHeight - window.visualViewport.height
          }px`
        }
      }, 100)
  }
}

function handleKeyboardWillShow(keyboardHeight) {
  // Add a small delay to ensure DOM is ready
  setTimeout(() => {
    console.log('Handling keyboard will SHOW')
    commentEditorBottom.value = keyboardHeight - DEFAULT_SAFE_AREA_INSET

    // Force layout recalculation
    document.body.style.position = 'fixed'
    document.body.style.overflow = 'hidden'

    const postViewModalContent = document.querySelector(
      '.post-view-modal'
    ) as HTMLElement
    const postContent = document.getElementById(
      'post-view-modal-content'
    ) as HTMLElement

    if (postViewModalContent && window.visualViewport) {
      postViewModalContent.style.maxHeight = `${window.visualViewport.height}px`
      postContent.style.maxHeight = `${window.visualViewport.height}px`
    }

    // Force redraw
    postViewModalContent?.offsetHeight
    postContent?.offsetHeight

    window.scrollTo({
      top: 0,
      behavior: 'smooth',
      left: 0,
    })
  }, 100)
}

function handleKeyboardWillHide() {
  // Add a small delay to ensure proper state cleanup
  setTimeout(() => {
    console.log('Handling keyboard will HIDE')
    commentEditorBottom.value = 0

    const postViewModalContent = document.querySelector(
      '.post-view-modal'
    ) as HTMLElement
    const postContent = document.getElementById(
      'post-view-modal-content'
    ) as HTMLElement

    if (postViewModalContent && window.visualViewport) {
      postViewModalContent.style.maxHeight = '100%'
      postContent.style.maxHeight = '100%'
    }

    // Force redraw

    postViewModalContent?.offsetHeight

    postContent?.offsetHeight

    document.body.style.position = ''
    document.body.style.overflow = 'scroll'
    document.body.style.overflowY = 'scroll'
  }, 100)
}

async function handleReplyToComment(parentAuthor: any) {
  await hapticsImpactMedium()
  showCommentReplyEditor(parentAuthor)
}

async function showEditCommentEditor() {
  // Emit event to close any existing editors
  emitter.emit('close:comment-editors')

  // we are doing this to rerender the editor everytime reply button is clicked
  showEditor.value = false
  editComment.value = true

  nextTick(() => {
    showEditor.value =
      appStore.isAuthenticated &&
      GroupState.state?.role &&
      props.comment?.user?.contactId === UserState.state.currentUser?.contactId
    if (!(appStore.isAuthenticated && GroupState.state?.role)) {
      showJoinGroupModal.value = true
    }
    if (showEditor.value) {
      emit('show:reply-comment-editor')
      if (app.state.isMobile) {
        emitter.emit('pwa:hide-comment-editor')
      }
    }
    if (!app.state.isMobile) {
      nextTick(() => {
        // Code to execute after rendering
        const commentEditor = document.getElementById(
          (props.comment._id as string) + '_editor'
        )
        if (commentEditor && commentCardRef.value) {
          commentCardRef.value.style.display = 'none'
          commentEditor.style.transform = 'scale(0.8)'
          commentEditor.style.transition =
            'transform 0.3s, scroll-behavior 0.2s'

          setTimeout(() => {
            commentEditor.scrollIntoView({
              behavior: 'smooth',
              block: 'center',
            })
            commentEditor.style.transform = 'scale(1)'
          }, 100)
        }
      })
    }
  })
}

function hideCommentEditor(hideEditor = true) {
  editComment.value = false
  showEditor.value = false
  commentCardRef.value!.style.display = 'flex'
  if (hideEditor) {
    emit('show:reply-comment-editor')
    if (app.state.isMobile) {
      emitter.emit('pwa:show-comment-editor')
    }
  }
}

async function toggleReplies() {
  showReplies.value = !showReplies.value
  if (showReplies.value) {
    await showCommentReplies()
  } else {
    hideCommentEditor(false)
    viewCommentText.value = `View ${props.comment.repliesCount} Replies`
  }
}

async function showCommentReplies() {
  showReplies.value = true
  fetchingReplies.value = true
  const replies = await CommentState.fetchComments(
    route.params.postId as string,
    {
      parentId: props.comment._id as string,
      limit: 20,
    }
  )
  viewCommentText.value = 'Hide Replies'
  props.comment['replies'] = replies
  fetchingReplies.value = false
}

async function loadMoreReplies(commentId: string) {
  if (props?.comment) {
    loadingMoreReplies.value = true
    await CommentState.fetchComments(route.params.postId as string, {
      parentId: commentId,
      previousId: CommentState.state.postCommentsNextPage.get(commentId),
      limit: 20,
    })
    loadingMoreReplies.value = false
  }
}

async function likeComment() {
  if (
    !UserState.state?.currentUser?.contactId ||
    !GroupState.isActiveMember()
  ) {
    showJoinGroupModal.value = true
    return
  }
  const payload: IReaction = {
    contentId: props.comment._id as string,
    contentType: ContentType.COMMENT,
    reactionType: ReactionType.LIKE,
    createdBy: UserState.state.currentUser.contactId,
    parentContentId: props.parentId || props.comment.postId,
  }
  const isAddReaction = await ReactionState.toggleReaction(
    payload,
    props.comment
  )
  if (isAddReaction != undefined) {
    addOrDeleteReaction(payload, isAddReaction)
  }
}

const addOrDeleteReaction = debounce(
  async (payload: IReaction, isAddReaction: boolean) => {
    await ReactionState.addOrDeleteReaction(
      payload,
      props.comment,
      isAddReaction
    )
  },
  1000
)

async function deleteComment() {
  await CommentState.deleteComment(
    props.comment as IComments,
    route.params.channelSlug as string
  )
  emit('delete-comment', props.comment)
}

async function reportComment() {
  try {
    await reportContent(
      props.comment._id as string,
      ContentType.COMMENT,
      GroupState.state.groupData._id,
      useAppStore().$state.locationId,
      UserState.state?.currentUser?.contactId as string,
      UserState.state?.currentUser?.fullName as string,
      props.parentPostId as string
    )
    notification.create({
      title: 'Comment has been reported!',
      type: 'success',
      duration: 3000,
    })
  } catch (_error) {
    notification.create({
      title: 'Something went wrong!',
      type: 'error',
      duration: 3000,
    })
  } finally {
    showReportConfirmationModal.value = false
  }
}

async function handleCommentAction(actionVal: string) {
  if (actionVal === 'deleteComment') {
    nextTick(() => {
      showDeleteConfirmationModal.value = true
    })
  } else if (actionVal === 'editComment') {
    nextTick(() => {
      showEditCommentEditor()
    })
  } else if (actionVal === 'copyComment') {
    if (isPwa()) {
      const shareableCommentLink = getShareableCommentLink(
        props.parentId || (props.parentPostId as string),
        props.comment,
        router
      )
      await Share.share({
        text: shareableCommentLink,
      }).catch(error => {
        console.error(error)
      })
    } else {
      await handleShareComment(
        props.parentId || (props.parentPostId as string),
        props.comment,
        router
      )
      notification.create({
        title: t('common.linkCopied'),
        type: 'success',
        duration: 3000,
      })
    }
  } else if (actionVal === 'reportComment') {
    showReportConfirmationModal.value = true
  }
  showCommentActionDrawer.value = false
}
function handleCommentActionClick() {
  nextTick(() => {
    showDeleteConfirmationModal.value = false
    if (!isPwa() && app.state.isMobile) {
      showCommentActionDrawer.value = true
    } else if (isPwa()) {
      showActions()
    }
  })
}

function updatePreview(index: number) {
  currentCommentIndex.value = index
  showCommentPreview.value = true
}

function onLikesClicked() {
  if (props.disableInteraction) {
    return
  }
  showLikesModal.value = true
}

const sanitizedFullName = computed(() => {
  return props.comment?.user?.fullName || 'Deleted User'
})

const sanitizedBody = computed(() => {
  const body = props.isTrimContent
    ? getTextContent(props.comment?.content)
    : props.comment?.content

  return body
})
</script>
<template>
  <div
    :id="comment._id"
    ref="commentCardRef"
    class="mt-1 flex flex-row overflow-hidden"
  >
    <div class="mr-4 cursor-pointer pb-2">
      <!-- <UIPopover
        placement="bottom-start"
        :show-arrow="false"
        raw
        :show="app.state.isMobile ? false : undefined"
      >
        <template #trigger>
          <div
            @click.stop="openUserProfile(comment?.user?.slug!, route, router)"
          >
            <BadgedAvatar
              :avatarUrl="comment?.user?.avatar"
              :fullName="comment?.user?.fullName"
              :showBadge="true"
              :role="authorRole"
              :level="comment?.user?.gamificationMeta?.level?.toString()"
            />
          </div>
        </template>
        <UserProfilePopover
          id="user_profile_tooltip"
          class="w-[460px]"
          :user="(comment.user as IUser)"
          :userRole="authorRole"
        />
      </UIPopover> -->
      <PopoverAvatar :user="comment?.user" :user-role="authorRole" />
    </div>
    <div class="w-[calc(100%-57px)]">
      <div
        class="rounded-xl border border border-solid border-communities-border bg-communities-fill p-3 !shadow-none"
        :class="{ highlighted: isCommentHighlighted }"
      >
        <div
          class="flex items-center justify-between text-communities-font-secondary"
        >
          <div
            class="user-profile text-md flex w-full items-center justify-start"
          >
            <div class="flex w-full flex-row justify-between">
              <div class="flex flex-row items-center gap-1">
                <UITextMdMedium
                  v-sanitized-html="sanitizedFullName"
                  :class="{
                    'hover:underline': comment.user && comment.user.fullName,
                    'text-slate-100': isCommentHighlighted,
                  }"
                  class="line-clamp-1 cursor-pointer text-communities-font-primary"
                  @click.stop="
                    openUserProfile(comment?.user?.slug, route, router)
                  "
                />

                <UITextXsRegular
                  class="line-clamp-1 shrink-0 font-normal text-communities-font-secondary"
                  :class="{
                    'text-slate-300': isCommentHighlighted,
                  }"
                  >•
                  {{ convertToRelativeTime(comment?.createdAt as string) }}
                  <span v-if="comment.lastEditedAt"
                    >({{ t('common.edited') }})</span
                  >
                </UITextXsRegular>
              </div>
              <div class="flex items-start">
                <div
                  v-if="app.state.isMobile && !props.disableInteraction"
                  id="drawer-trigger"
                  class="flex h-7 w-7 items-center justify-center rounded-full"
                  tabindex="0"
                  role="button"
                  aria-label="Comment Actions"
                  @click.stop="handleCommentActionClick"
                  @keyup.enter="handleCommentActionClick"
                >
                  <DotsHorizontalIcon class="w-5" />
                </div>
                <UIDropdown
                  v-else-if="!props.disableInteraction"
                  id="comment-action-dropdown"
                  :options="CommentActionOption"
                  class="w-20"
                  placement="top-end"
                  @select="handleCommentAction"
                >
                  <div
                    id="post-action-dropdown-trigger"
                    class="flex h-6 w-6 items-center justify-center rounded-full text-communities-font-secondary hover:bg-communities-surface hover:text-communities-font-primary"
                    tabindex="0"
                    role="button"
                    aria-label="Comment Actions"
                    @click.stop="handleCommentActionClick"
                    @keyup.enter="handleCommentActionClick"
                  >
                    <DotsHorizontalIcon class="w-5" />
                  </div>
                </UIDropdown>
              </div>
            </div>
          </div>
        </div>
        <div
          class="comment-body"
          :class="{ 'line-clamp-2': props.isTrimContent }"
        >
          <div ref="commentContentRef" class="comment-content">
            <UITextMdNormal
              v-html="sanitizedBody"
              class="content-description break-words"
              :class="
                isCommentHighlighted
                  ? 'text-slate-200'
                  : 'text-communities-font-primary'
              "
            />
          </div>
          <div
            v-if="comment?.attachments && comment.attachments.length > 0"
            class="comment-attachments flex overflow-x-scroll py-2"
          >
            <div v-for="(file, index) in comment.attachments" :key="file.id">
              <CommentsAttachments
                :file="file"
                :index="index"
                source="comments"
                class="mr-4"
                :total-attachments="comment?.attachments?.length"
                @update:show="updatePreview"
              />
            </div>
          </div>
        </div>
      </div>
      <div
        v-if="props.isShowCommentAction"
        class="item-center mx-1 flex gap-1 text-sm text-communities-font-secondary"
      >
        <div class="flex items-center justify-center">
          <LikeButton
            :content="comment"
            :like-action="likeComment"
            class="ml-3 py-2 font-bold"
          />
          <div
            class="-translate-x-3 cursor-pointer text-start text-communities-font-secondary hover:rounded-full hover:text-communities-font-primary"
          >
            <UIPopover
              placement="bottom-start"
              :show-arrow="false"
              raw
              :show="app.state.isMobile ? false : undefined"
            >
              <template #trigger>
                <UITextSmMedium
                  class="p-2 font-medium"
                  @click.stop="onLikesClicked"
                >
                  {{ getNumberAbbreviation(comment?.reactions!['LIKE'] || 0) }}
                </UITextSmMedium>
              </template>
              <ReactionsList
                :content-id="(comment._id as string)"
                :reaction-type="ReactionType.LIKE"
                :content-reactions-count="comment.reactions!['LIKE'] || 0"
              />
            </UIPopover>
          </div>
          <div v-if="showLikesModal">
            <ReactionsView
              :content-id="(comment._id as string)"
              :reaction-type="ReactionType.LIKE"
              :content-reactions-count="comment.reactions!['LIKE'] || 0"
              :show-modal="showLikesModal"
              @update:show="showLikesModal = false"
            />
          </div>
        </div>
        <div
          v-if="!ChannelState.state.hasAnnouncementRestriction"
          class="flex items-center text-communities-font-secondary"
        >
          <div
            id="show-comment_editor__btn"
            :text="true"
            class="flex cursor-pointer items-center rounded-md p-2 hover:bg-communities-fill md:hover:text-communities-font-primary"
            tabindex="0"
            role="button"
            aria-label="Reply to Comment"
            @click="handleReplyToComment(comment?.user)"
            @keyup.enter="handleReplyToComment(comment?.user)"
          >
            <!-- <Share06Icon v-if="!app.state.isMobile" class="my-2 mr-4 h-4 w-4" /> -->
            <UITextSmMedium class="font-medium">
              {{ t('common.reply') }}
            </UITextSmMedium>
          </div>
        </div>
      </div>
      <div
        v-if="comment?.repliesCount"
        class="mb-1 flex items-center pl-2 text-communities-font-secondary"
      >
        <div
          id="show-comment_editor__btn"
          :text="true"
          class="flex cursor-pointer items-center hover:text-communities-font-primary"
          :loading="fetchingReplies"
          tabindex="0"
          role="button"
          aria-label="View Replies"
          @click="toggleReplies()"
          @keyup.enter="toggleReplies()"
        >
          <FlipBackwardIcon v-if="!app.state.isMobile" class="h-4 w-4" />
          <UITextSmMedium class="p-2 font-medium">
            {{ viewCommentText }}
          </UITextSmMedium>
        </div>
      </div>
      <div v-if="showReplies">
        <div>
          <CommentCard
            v-for="(childComment, _index) in replies"
            v-if="!fetchingReplies && comment?.replies?.length"
            :key="childComment._id"
            :comment="childComment"
            :parent-id="comment._id"
            :parent-post-id="props.parentPostId"
            :mention-user-tooltip="mentionUserTooltip"
            @show:childCommentEditor="handleReplyToComment"
          />
        </div>
        <div
          v-if="!fetchingReplies && repliesNextPage"
          class="flex items-center justify-start pl-4 text-communities-font-secondary"
        >
          <UIButton
            id="show-comment_editor__btn"
            :text="true"
            class="font-sans"
            :loading="loadingMoreReplies"
            @click="loadMoreReplies(comment?._id as string)"
          >
            <div
              class="fontFamily-sans p-2 text-left font-sans text-xs font-medium"
            >
              {{ t('comments.loadMoreReplies') }}
            </div>
          </UIButton>
        </div>
      </div>
    </div>
  </div>

  <div v-if="showEditor" :id="comment._id + '_editor'" class="my-3">
    <CommentEditor
      :user="currentUser"
      :enable-comment="true"
      :parent-id="parentId || comment?._id"
      :parent-author="replyToCommentAuthor"
      :comment-object="editComment ? comment : undefined"
      :comment-editor-bottom="commentEditorBottom"
      @update:show="hideCommentEditor"
    />
  </div>
  <UIConfirmation
    :show="showDeleteConfirmationModal"
    :title="t('comments.deleteComment')"
    :message="t('comments.deleteCommentMessage')"
    type="error"
    button-type="error"
    @update:show="showDeleteConfirmationModal = $event"
    @update:confirmed="deleteComment"
  />
  <UIConfirmation
    :show="showReportConfirmationModal"
    :title="t('comments.reportComment')"
    :message="t('comments.reportCommentMessage')"
    type="error"
    button-type="error"
    @update:show="showReportConfirmationModal = $event"
    @update:confirmed="reportComment"
  />
  <AttachmentsPreview
    :show-modal="showCommentPreview"
    :file="comment?.attachments && comment.attachments[currentCommentIndex]"
    :current-index="currentCommentIndex"
    :total-attachments="comment?.attachments?.length"
    @update:close="showCommentPreview = false"
    @update:show="updatePreview"
  />
  <JoinGroupModal
    :show="showJoinGroupModal"
    @update:show="showJoinGroupModal = false"
  />
  <UIDrawer
    id="post-options-drawer"
    v-model:show="showCommentActionDrawer"
    class="rounded-t-xl p-0"
    :placement="'bottom'"
    :height="
      CommentActionOption.length * 80 - (CommentActionOption.length - 1) * 10
    "
  >
    <UIDrawerContent
      id="post-options-drawer-menu"
      v-swipe="{
        direction: 'down',
        callback: () => (showCommentActionDrawer = !showCommentActionDrawer),
      }"
      class="p-0"
      :closable="false"
    >
      <div class="mt-2 flex h-1 w-full flex-col items-center justify-center">
        <div
          class="h-1 w-16 rounded-md border border-solid border-communities-border bg-communities-border"
        />
      </div>
      <UIMenu
        id="post-options-menu-mobile"
        :options="CommentActionOptionsMobile"
        :on-update:value="handleCommentAction"
      />
    </UIDrawerContent>
  </UIDrawer>
  <!-- //TODO change to loggedIn user -->
</template>
<style scoped>
.comment-attachments::-webkit-scrollbar {
  display: none;
}
.comment-attachments {
  max-width: calc(100vw - 150px);
}

.highlighted {
  background-color: var(--primaryLight);
  transition: background-color 3s ease-in-out;
}

::v-deep .n-spin {
  color: var(--fontSecondary);
}
.content-description {
  max-width: calc(100vw - 100px);
}
</style>
